import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MousePointer2,
  Square,
  Circle as CircleIcon,
  Slash,
  Box,
  Trash2,
} from "lucide-react";
import type { GardenSceneFeature, GardenSceneGraph } from "@shared/schema";

type Tool = "select" | "bed" | "plant" | "fence" | "structure";

type FeatureType = GardenSceneFeature["type"];

interface ToolDef {
  id: Tool;
  label: string;
  icon: typeof Square;
  featureType?: FeatureType;
  defaultWidth?: number;
  defaultHeight?: number;
  defaultLabel?: string;
}

const TOOLS: ToolDef[] = [
  { id: "select", label: "Select", icon: MousePointer2 },
  {
    id: "bed",
    label: "Bed",
    icon: Square,
    featureType: "bed",
    defaultWidth: 6,
    defaultHeight: 3,
    defaultLabel: "Bed",
  },
  {
    id: "plant",
    label: "Plant",
    icon: CircleIcon,
    featureType: "tree",
    defaultWidth: 5,
    defaultHeight: 5,
    defaultLabel: "Tree",
  },
  {
    id: "fence",
    label: "Fence",
    icon: Slash,
    featureType: "fence",
    defaultWidth: 10,
    defaultHeight: 0.3,
    defaultLabel: "Fence",
  },
  {
    id: "structure",
    label: "Structure",
    icon: Box,
    featureType: "shed",
    defaultWidth: 6,
    defaultHeight: 6,
    defaultLabel: "Shed",
  },
];

const FEATURE_TYPE_OPTIONS: { type: FeatureType; label: string }[] = [
  { type: "bed", label: "Garden bed" },
  { type: "fence", label: "Fence" },
  { type: "path", label: "Path" },
  { type: "tree", label: "Tree" },
  { type: "shed", label: "Shed / structure" },
  { type: "existing_plant", label: "Existing plant" },
  { type: "lawn", label: "Lawn" },
  { type: "water", label: "Water feature" },
  { type: "obstacle", label: "Obstacle" },
  { type: "door", label: "Door" },
  { type: "edge", label: "Edge / boundary" },
];

const TYPE_FILL: Record<FeatureType, string> = {
  bed: "#8a6a42",
  fence: "#4a3a2a",
  path: "#d6c9a8",
  tree: "#3f7a4a",
  shed: "#8a8a8a",
  existing_plant: "#6a9a4a",
  lawn: "#b7d39a",
  water: "#6ea6c9",
  obstacle: "#b05050",
  door: "#a07040",
  edge: "#666",
};

const HANDLE_PX = 22; // diameter of resize handles in screen pixels
const MIN_FT = 0.5;

interface Props {
  initial?: GardenSceneGraph | null;
  worldWidthFt: number;
  worldHeightFt: number;
  cardinalSouthDegrees: number | null;
  onSave: (features: GardenSceneFeature[]) => void;
  saving?: boolean;
}

interface DragState {
  kind: "move" | "resize-se" | "resize-radius";
  featureId: string;
  pointerId: number;
  startWorld: { x: number; y: number };
  startFeature: GardenSceneFeature;
}

function compassLabel(degrees: number | null) {
  const s = ((degrees ?? 180) % 360 + 360) % 360;
  const dirs = ["S", "W", "N", "E"];
  const idx = Math.round(s / 90) % 4;
  return {
    top: dirs[idx],
    right: dirs[(idx + 1) % 4],
    bottom: dirs[(idx + 2) % 4],
    left: dirs[(idx + 3) % 4],
  };
}

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

function isCircleType(type: FeatureType) {
  return type === "tree" || type === "existing_plant";
}

function clampRect(
  feature: GardenSceneFeature,
  worldW: number,
  worldH: number,
): GardenSceneFeature {
  const w = Math.max(MIN_FT, Math.min(feature.worldWidth, worldW));
  const h = Math.max(MIN_FT, Math.min(feature.worldHeight, worldH));
  const x = Math.max(0, Math.min(feature.worldX, worldW - w));
  const y = Math.max(0, Math.min(feature.worldY, worldH - h));
  return { ...feature, worldX: x, worldY: y, worldWidth: w, worldHeight: h };
}

function hitTest(
  features: GardenSceneFeature[],
  worldX: number,
  worldY: number,
): GardenSceneFeature | null {
  for (let i = features.length - 1; i >= 0; i--) {
    const f = features[i];
    if (
      worldX >= f.worldX &&
      worldX <= f.worldX + f.worldWidth &&
      worldY >= f.worldY &&
      worldY <= f.worldY + f.worldHeight
    ) {
      return f;
    }
  }
  return null;
}

export function GardenSketchEditor({
  initial,
  worldWidthFt,
  worldHeightFt,
  cardinalSouthDegrees,
  onSave,
  saving,
}: Props) {
  const [features, setFeatures] = useState<GardenSceneFeature[]>(
    () => initial?.features ?? [],
  );
  const [tool, setTool] = useState<Tool>("select");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const compass = compassLabel(cardinalSouthDegrees);

  const selected = useMemo(
    () => features.find((f) => f.id === selectedId) ?? null,
    [features, selectedId],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setRenderedSize({ width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const renderedW = renderedSize?.width ?? 0;
  const renderedH = renderedSize?.height ?? 0;
  const pxPerFt = renderedW > 0 ? renderedW / worldWidthFt : 20;
  const handleFt = HANDLE_PX / Math.max(1, pxPerFt);

  // Convert client (page) coords to world feet.
  const worldFromClient = (clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
    const sx = (clientX - rect.left) / rect.width;
    const sy = (clientY - rect.top) / rect.height;
    return {
      x: sx * worldWidthFt,
      // SVG y grows down; world y grows north (= up). Flip.
      y: worldHeightFt - sy * worldHeightFt,
    };
  };

  const placeFeature = (worldX: number, worldY: number, def: ToolDef) => {
    if (!def.featureType) return;
    const w = def.defaultWidth ?? 4;
    const h = def.defaultHeight ?? 4;
    const f: GardenSceneFeature = {
      id: newId(),
      type: def.featureType,
      label: def.defaultLabel ?? def.featureType,
      worldX: worldX - w / 2,
      worldY: worldY - h / 2,
      worldWidth: w,
      worldHeight: h,
      rotationDeg: 0,
      confidence: "high",
    };
    const clamped = clampRect(f, worldWidthFt, worldHeightFt);
    setFeatures((prev) => [...prev, clamped]);
    setSelectedId(clamped.id);
    setTool("select");
  };

  const onCanvasPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const w = worldFromClient(e.clientX, e.clientY);
    if (tool !== "select") {
      const def = TOOLS.find((t) => t.id === tool);
      if (def) placeFeature(w.x, w.y, def);
      return;
    }
    const hit = hitTest(features, w.x, w.y);
    setSelectedId(hit?.id ?? null);
    if (!hit) return;
    e.preventDefault();
    dragRef.current = {
      kind: "move",
      featureId: hit.id,
      pointerId: e.pointerId,
      startWorld: w,
      startFeature: hit,
    };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };

  const onHandlePointerDown = (
    feature: GardenSceneFeature,
    kind: DragState["kind"],
  ) => (e: React.PointerEvent<SVGCircleElement | SVGRectElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const w = worldFromClient(e.clientX, e.clientY);
    dragRef.current = {
      kind,
      featureId: feature.id,
      pointerId: e.pointerId,
      startWorld: w,
      startFeature: feature,
    };
    const target = e.currentTarget as unknown as Element;
    target.setPointerCapture?.(e.pointerId);
  };

  const onCanvasPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const w = worldFromClient(e.clientX, e.clientY);
    const dx = w.x - drag.startWorld.x;
    const dy = w.y - drag.startWorld.y;
    setFeatures((prev) =>
      prev.map((f) => {
        if (f.id !== drag.featureId) return f;
        if (drag.kind === "move") {
          return clampRect(
            {
              ...drag.startFeature,
              worldX: drag.startFeature.worldX + dx,
              worldY: drag.startFeature.worldY + dy,
            },
            worldWidthFt,
            worldHeightFt,
          );
        }
        if (drag.kind === "resize-se") {
          // SE handle: drag east+south. World y decreases as you drag south.
          return clampRect(
            {
              ...drag.startFeature,
              worldWidth: Math.max(MIN_FT, drag.startFeature.worldWidth + dx),
              worldHeight: Math.max(MIN_FT, drag.startFeature.worldHeight - dy),
              worldY: Math.max(
                0,
                drag.startFeature.worldY + Math.min(0, dy),
              ),
            },
            worldWidthFt,
            worldHeightFt,
          );
        }
        if (drag.kind === "resize-radius") {
          const cx = drag.startFeature.worldX + drag.startFeature.worldWidth / 2;
          const cy = drag.startFeature.worldY + drag.startFeature.worldHeight / 2;
          const radius = Math.max(MIN_FT / 2, Math.hypot(w.x - cx, w.y - cy));
          const size = radius * 2;
          return clampRect(
            {
              ...drag.startFeature,
              worldX: cx - radius,
              worldY: cy - radius,
              worldWidth: size,
              worldHeight: size,
            },
            worldWidthFt,
            worldHeightFt,
          );
        }
        return f;
      }),
    );
  };

  const onCanvasPointerUp = () => {
    dragRef.current = null;
  };

  const updateSelected = (patch: Partial<GardenSceneFeature>) => {
    if (!selectedId) return;
    setFeatures((prev) =>
      prev.map((f) => (f.id === selectedId ? { ...f, ...patch } : f)),
    );
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setFeatures((prev) => prev.filter((f) => f.id !== selectedId));
    setSelectedId(null);
  };

  // Convert world (x,y) to SVG (x,y). SVG y grows down, world y grows north.
  const toSvg = (wx: number, wy: number) => ({
    x: wx * pxPerFt,
    y: (worldHeightFt - wy) * pxPerFt,
  });

  const svgWidth = worldWidthFt * pxPerFt;
  const svgHeight = worldHeightFt * pxPerFt;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <Button
              key={t.id}
              type="button"
              size="sm"
              variant={tool === t.id ? "default" : "outline"}
              onClick={() => setTool(t.id)}
            >
              <Icon className="mr-1 h-4 w-4" />
              {t.label}
            </Button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        {tool === "select"
          ? "Tap a feature to select. Drag the body to move, drag the corner handle to resize."
          : `Tap on the canvas to drop a ${TOOLS.find((t) => t.id === tool)?.label.toLowerCase()}.`}
      </p>

      <div
        ref={containerRef}
        className="relative w-full touch-none select-none overflow-hidden rounded-md border bg-[#f4efe4]"
        style={{ aspectRatio: `${worldWidthFt} / ${worldHeightFt}` }}
      >
        {renderedSize ? (
          <svg
            width={renderedW}
            height={renderedH}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            preserveAspectRatio="xMidYMid meet"
            onPointerDown={onCanvasPointerDown}
            onPointerMove={onCanvasPointerMove}
            onPointerUp={onCanvasPointerUp}
            onPointerCancel={onCanvasPointerUp}
            className="absolute inset-0 h-full w-full"
          >
            <defs>
              <pattern id="sketch-grid" width={pxPerFt} height={pxPerFt} patternUnits="userSpaceOnUse">
                <path
                  d={`M ${pxPerFt} 0 L 0 0 0 ${pxPerFt}`}
                  fill="none"
                  stroke="#d8c9ae"
                  strokeWidth={0.5}
                />
              </pattern>
              <pattern id="sketch-soil" width={4} height={4} patternUnits="userSpaceOnUse">
                <rect width={4} height={4} fill="#ece2cc" />
                <circle cx={1} cy={1} r={0.5} fill="#c8b893" />
              </pattern>
            </defs>
            <rect
              x={0}
              y={0}
              width={svgWidth}
              height={svgHeight}
              fill="url(#sketch-soil)"
            />
            <rect
              x={0}
              y={0}
              width={svgWidth}
              height={svgHeight}
              fill="url(#sketch-grid)"
            />

            {features.map((f) => {
              const { x, y } = toSvg(f.worldX, f.worldY + f.worldHeight);
              const w = f.worldWidth * pxPerFt;
              const h = f.worldHeight * pxPerFt;
              const isSelected = f.id === selectedId;
              const stroke = isSelected ? "#1b5e20" : "rgba(0,0,0,0.4)";
              const strokeWidth = isSelected ? 2 : 1;
              if (isCircleType(f.type)) {
                const cx = x + w / 2;
                const cy = y + h / 2;
                const r = Math.min(w, h) / 2;
                return (
                  <circle
                    key={f.id}
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill={TYPE_FILL[f.type]}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    opacity={0.85}
                  />
                );
              }
              return (
                <rect
                  key={f.id}
                  x={x}
                  y={y}
                  width={w}
                  height={h}
                  fill={TYPE_FILL[f.type]}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  opacity={0.85}
                  rx={f.type === "bed" || f.type === "shed" ? 3 : 0}
                />
              );
            })}

            {selected ? (
              isCircleType(selected.type) ? (
                (() => {
                  const { x, y } = toSvg(
                    selected.worldX,
                    selected.worldY + selected.worldHeight,
                  );
                  const w = selected.worldWidth * pxPerFt;
                  const h = selected.worldHeight * pxPerFt;
                  const cx = x + w / 2;
                  const cy = y + h / 2;
                  const r = Math.min(w, h) / 2;
                  return (
                    <circle
                      cx={cx + r}
                      cy={cy}
                      r={HANDLE_PX / 2}
                      fill="#fff"
                      stroke="#1b5e20"
                      strokeWidth={2}
                      onPointerDown={onHandlePointerDown(selected, "resize-radius")}
                      style={{ cursor: "ew-resize" }}
                    />
                  );
                })()
              ) : (
                (() => {
                  const { x, y } = toSvg(
                    selected.worldX,
                    selected.worldY + selected.worldHeight,
                  );
                  const w = selected.worldWidth * pxPerFt;
                  const h = selected.worldHeight * pxPerFt;
                  return (
                    <rect
                      x={x + w - HANDLE_PX / 2}
                      y={y + h - HANDLE_PX / 2}
                      width={HANDLE_PX}
                      height={HANDLE_PX}
                      fill="#fff"
                      stroke="#1b5e20"
                      strokeWidth={2}
                      rx={4}
                      onPointerDown={onHandlePointerDown(selected, "resize-se")}
                      style={{ cursor: "se-resize" }}
                    />
                  );
                })()
              )
            ) : null}

            <g
              fontSize={Math.min(14, Math.max(10, pxPerFt * 0.6))}
              fontWeight={600}
              fill="#555"
              textAnchor="middle"
            >
              <text x={svgWidth / 2} y={pxPerFt * 0.5}>{compass.top}</text>
              <text x={svgWidth / 2} y={svgHeight - pxPerFt * 0.2}>{compass.bottom}</text>
              <text x={pxPerFt * 0.4} y={svgHeight / 2 + 4} textAnchor="start">{compass.left}</text>
              <text x={svgWidth - pxPerFt * 0.4} y={svgHeight / 2 + 4} textAnchor="end">{compass.right}</text>
            </g>
          </svg>
        ) : null}
      </div>

      {selected ? (
        <div className="space-y-3 rounded-md border bg-muted/30 p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Edit feature</div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={deleteSelected}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Delete
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Type</Label>
              <select
                value={selected.type}
                onChange={(e) => updateSelected({ type: e.target.value as FeatureType })}
                className="mt-1 w-full rounded border bg-background px-2 py-1.5 text-sm"
              >
                {FEATURE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.type} value={opt.type}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={selected.label}
                onChange={(e) => updateSelected({ label: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>
              <Label className="text-xs">Width</Label>
              <Input
                type="number"
                step="0.5"
                value={selected.worldWidth.toFixed(1)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) {
                    updateSelected({
                      worldWidth: Math.max(MIN_FT, Math.min(v, worldWidthFt)),
                    });
                  }
                }}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Height</Label>
              <Input
                type="number"
                step="0.5"
                value={selected.worldHeight.toFixed(1)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) {
                    updateSelected({
                      worldHeight: Math.max(MIN_FT, Math.min(v, worldHeightFt)),
                    });
                  }
                }}
                className="mt-1"
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          {features.length} feature{features.length === 1 ? "" : "s"}
        </div>
        <Button onClick={() => onSave(features)} disabled={saving}>
          {saving ? "Saving…" : "Save and continue"}
        </Button>
      </div>
    </div>
  );
}
